import { useState, useMemo, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { parseArabicNumber, formatArabicNumber } from '@/utils/numberUtils';

const CONVERSION_RATE = 100;
const NEW_DENOMINATIONS = [500, 200, 100, 50, 25, 10];

export const CurrencyCalculator = ({ exampleData, onSuggestionsChange }) => {
  const [price, setPrice] = useState('');
  const [priceCurrency, setPriceCurrency] = useState('old');
  const [payment1, setPayment1] = useState('');
  const [payment1Currency, setPayment1Currency] = useState('new');
  const [payment2, setPayment2] = useState('');
  const [payment2Currency, setPayment2Currency] = useState('old');
  const [showPayment2, setShowPayment2] = useState(false);
  const [suggestionFeedback, setSuggestionFeedback] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Apply example data when provided
  useEffect(() => {
    if (exampleData) {
      // Reset first
      setResult(null);
      setError('');
      
      // Apply example values
      setPrice(exampleData.price);
      setPriceCurrency(exampleData.priceCurrency);
      setPayment1(exampleData.payment1);
      setPayment1Currency(exampleData.payment1Currency);
      setPayment2(exampleData.payment2);
      setPayment2Currency(exampleData.payment2Currency);
      setShowPayment2(exampleData.showPayment2);
    }
  }, [exampleData]);

  const parseToOld = (value, currency) => {
    if (!value || value.trim() === '') return 0;
    const num = parseArabicNumber(value);
    return currency === 'new' ? Math.round(num * CONVERSION_RATE) : Math.round(num);
  };

  // Calculate live equivalent for Price input
  const priceEquivalent = useMemo(() => {
    const priceValue = parseArabicNumber(price);
    if (!priceValue || priceValue <= 0) return null;

    if (priceCurrency === 'old') {
      // Old to New: divide by 100, show up to 2 decimals, remove trailing zeros
      const equivalentNew = priceValue / CONVERSION_RATE;
      const formatted = equivalentNew.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
      return { value: formatted, currency: 'جديد' };
    } else {
      // New to Old: multiply by 100, integer
      const equivalentOld = Math.round(priceValue * CONVERSION_RATE);
      const formatted = equivalentOld.toLocaleString('en-US');
      return { value: formatted, currency: 'قديم' };
    }
  }, [price, priceCurrency]);

  // Generate smart payment suggestions
  const paymentSuggestions = useMemo(() => {
    const priceInOld = parseToOld(price, priceCurrency);
    
    if (priceInOld <= 0) {
      return [];
    }

    // If price < 100, only show "Old only" option
    if (priceInOld < 100) {
      return [{
        newAmount: 0,
        oldAmount: priceInOld,
        key: `0-${priceInOld}`,
        type: 'old-only'
      }];
    }

    const baseNew = Math.floor(priceInOld / 100);
    const remainderOld = priceInOld % 100;
    
    const allSuggestions = [];
    const seen = new Set();

    const addSuggestion = (newAmount, oldAmount, type) => {
      const key = `${newAmount}-${oldAmount}`;
      if (!seen.has(key) && oldAmount >= 0) {
        seen.add(key);
        allSuggestions.push({ newAmount, oldAmount, key, type });
        return true;
      }
      return false;
    };

    // 1. Add "New only" suggestion
    addSuggestion(baseNew, remainderOld, 'new-only');

    // 2. Generate denomination-based suggestions
    for (const denom of NEW_DENOMINATIONS) {
      const newAmount = Math.floor(baseNew / denom) * denom;
      const oldAmount = priceInOld - (newAmount * 100);
      if (newAmount > 0 && newAmount < baseNew) {
        addSuggestion(newAmount, oldAmount, 'denomination');
      }
    }

    // 3. Generate 4 mid-level suggestions using percent targets
    const percentTargets = [0.8, 0.6, 0.4, 0.2];
    const midLevelSuggestions = [];
    
    for (const p of percentTargets) {
      const target = Math.floor(baseNew * p);
      let candidateNew = Math.floor(target / 5) * 5; // Snap to multiples of 5
      
      // 15 is not feasible with denominations 10 and 25
      if (candidateNew === 15) {
        candidateNew = 10;
      }
      
      const candidateOld = priceInOld - (candidateNew * 100);
      
      if (candidateNew >= 10 && candidateNew < baseNew && candidateOld >= 0) {
        const key = `${candidateNew}-${candidateOld}`;
        if (!seen.has(key)) {
          seen.add(key);
          midLevelSuggestions.push({
            newAmount: candidateNew,
            oldAmount: candidateOld,
            key,
            type: 'mid-level'
          });
        }
      }
    }

    // 4. Add "Old only" option
    addSuggestion(0, priceInOld, 'old-only');

    // 5. Smart sorting and selection to ensure variety
    const newOnly = allSuggestions.filter(s => s.type === 'new-only');
    const oldOnly = allSuggestions.filter(s => s.type === 'old-only');
    const denomination = allSuggestions.filter(s => s.type === 'denomination')
      .sort((a, b) => b.newAmount - a.newAmount);
    
    // Keep up to 3 distinct mid-level suggestions
    const midLevel = midLevelSuggestions.slice(0, 3);
    
    // Build final list with variety
    const finalSuggestions = [];
    
    // 1. New only (if exists)
    if (newOnly.length > 0) {
      finalSuggestions.push(newOnly[0]);
    }
    
    // 2. Add mid-level suggestions (up to 3)
    finalSuggestions.push(...midLevel);
    
    // 3. Fill remaining slots with denomination-based (highest newAmount first)
    const remainingSlots = 7 - finalSuggestions.length - (oldOnly.length > 0 ? 1 : 0);
    for (let i = 0; i < Math.min(remainingSlots, denomination.length); i++) {
      finalSuggestions.push(denomination[i]);
    }
    
    // 4. Old only last (if exists and room available)
    if (oldOnly.length > 0 && finalSuggestions.length < 7) {
      finalSuggestions.push(oldOnly[0]);
    }

    return finalSuggestions.slice(0, 7);
  }, [price, priceCurrency]);

  // Expose suggestions to parent component
  useEffect(() => {
    if (onSuggestionsChange) {
      onSuggestionsChange(paymentSuggestions);
    }
  }, [paymentSuggestions, onSuggestionsChange]);

  const handleSuggestionClick = (suggestion) => {
    // CRITICAL: Reset payment fields first
    setPayment1('');
    setPayment2('');
    setPayment1Currency('new');
    setPayment2Currency('old');
    setShowPayment2(false);
    setError('');
    setResult(null);

    // Fill Payment #1 with New amount if > 0
    if (suggestion.newAmount > 0) {
      setPayment1(String(suggestion.newAmount));
      setPayment1Currency('new');
    }

    // Fill Payment #2 with Old amount if > 0
    if (suggestion.oldAmount > 0) {
      setPayment2(String(suggestion.oldAmount));
      setPayment2Currency('old');
      setShowPayment2(true);
    }

    // Show feedback
    setSuggestionFeedback('تم تعبئة حقول الدفع');
    setTimeout(() => {
      setSuggestionFeedback('');
    }, 2000);
  };

  const calculateBreakdown = (changeInOld) => {
    const breakdown = [];
    let remaining = changeInOld;

    for (let denom of NEW_DENOMINATIONS) {
      const denomInOld = denom * CONVERSION_RATE;
      const count = Math.floor(remaining / denomInOld);
      
      if (count > 0) {
        breakdown.push({
          count: count,
          denomination: denom,
          currency: 'جديد'
        });
        remaining -= count * denomInOld;
      }
    }

    if (remaining > 0) {
      breakdown.push({
        count: null,
        denomination: remaining,
        currency: 'قديم',
        isRemainder: true
      });
    }

    return breakdown;
  };

  const handleCalculate = () => {
    setError('');
    setResult(null);

    const priceInOld = parseToOld(price, priceCurrency);
    const payment1InOld = parseToOld(payment1, payment1Currency);
    const payment2InOld = showPayment2 ? parseToOld(payment2, payment2Currency) : 0;

    if (priceInOld <= 0) {
      setError('الرجاء إدخال المبلغ المطلوب');
      return;
    }

    if (payment1InOld <= 0 && payment2InOld <= 0) {
      setError('الرجاء إدخال المبلغ المدفوع');
      return;
    }

    const totalPaidInOld = payment1InOld + payment2InOld;
    const changeInOld = totalPaidInOld - priceInOld;

    if (changeInOld < 0) {
      // Insufficient payment - calculate missing amounts
      const missingOldOnly = priceInOld - totalPaidInOld;
      const missingNewOnly = Math.ceil(missingOldOnly / CONVERSION_RATE);
      
      setError('المبلغ المدفوع غير كافٍ');
      setResult({
        insufficient: true,
        missingOldOnly,
        missingNewOnly
      });
      return;
    }

    const changeInNew = changeInOld / CONVERSION_RATE;
    const breakdown = changeInOld > 0 ? calculateBreakdown(changeInOld) : null;

    setResult({
      insufficient: false,
      changeInNew,
      changeInOld,
      breakdown
    });
  };

  const handleReset = () => {
    setPrice('');
    setPriceCurrency('old');
    setPayment1('');
    setPayment1Currency('old');
    setPayment2('');
    setPayment2Currency('old');
    setShowPayment2(false);
    setResult(null);
    setError('');
    setSuggestionFeedback('');
  };

  const handleInputChange = (setter) => (e) => {
    setError('');
    setter(e.target.value);
  };

  return (
    <div className="calculator-card">
      {/* Export handleSuggestionClick for external use */}
      {typeof window !== 'undefined' && (() => {
        window.__currencyCalcHandleSuggestion = handleSuggestionClick;
        return null;
      })()}

      {/* Price Input */}
      <div className="input-group price-input-group">
        <label htmlFor="price" className="label">المبلغ المطلوب</label>
        <div className="input-wrapper price-input-wrapper">
          <input
            type="text"
            id="price"
            className="input"
            placeholder="أدخل سعر الفاتورة المطلوب"
            value={price}
            onChange={handleInputChange(setPrice)}
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <select
            id="priceCurrency"
            className="currency-select"
            value={priceCurrency}
            onChange={(e) => setPriceCurrency(e.target.value)}
          >
            <option value="old">قديم</option>
            <option value="new">جديد</option>
          </select>
        </div>
        {/* Live Equivalent Preview */}
        {priceEquivalent && (
          <div className="text-xs text-gray-500 mt-1 font-medium" style={{ fontSize: '0.75rem', color: 'hsl(var(--text-muted))', marginTop: '0.25rem' }}>
            ما يعادل: <span dir="ltr">{priceEquivalent.value}</span> {priceEquivalent.currency}
          </div>
        )}
      </div>

      {/* Payment 1 Input */}
      <div className="input-group">
        <label htmlFor="payment1" className="label">المبلغ المدفوع</label>
        <div className="input-wrapper">
          <input
            type="text"
            id="payment1"
            className="input"
            placeholder="أدخل المبلغ الذي تريد دفعه"
            value={payment1}
            onChange={handleInputChange(setPayment1)}
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <select
            id="payment1Currency"
            className="currency-select"
            value={payment1Currency}
            onChange={(e) => setPayment1Currency(e.target.value)}
          >
            <option value="old">قديم</option>
            <option value="new">جديد</option>
          </select>
        </div>
      </div>

      {/* Payment 2 Input (Conditional) */}
      {showPayment2 && (
        <div className="input-group">
          <label htmlFor="payment2" className="label">مبلغ إضافي</label>
          <div className="input-wrapper">
            <input
              type="text"
              id="payment2"
              className="input"
              placeholder="أدخل مبلغ إضافي"
              value={payment2}
              onChange={handleInputChange(setPayment2)}
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <select
              id="payment2Currency"
              className="currency-select"
              value={payment2Currency}
              onChange={(e) => setPayment2Currency(e.target.value)}
            >
              <option value="old">قديم</option>
              <option value="new">جديد</option>
            </select>
          </div>
        </div>
      )}

      {/* Add Payment Button */}
      <button
        className="btn-secondary"
        onClick={() => setShowPayment2(!showPayment2)}
      >
        {showPayment2 ? '− إزالة المبلغ الإضافي' : '+ إضافة مبلغ آخر'}
      </button>

      {/* Action Buttons */}
      <div className="button-group">
        <button className="btn-primary" onClick={handleCalculate}>
          احسب
        </button>
        <button className="btn-outline btn-clear" onClick={handleReset}>
          <Trash2 size={20} className="trash-icon" />
          مسح
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Suggestion Feedback */}
      {suggestionFeedback && (
        <div className="suggestion-feedback">
          {suggestionFeedback}
        </div>
      )}

      {/* Result Section */}
      {result && (
        <div className="result-section">
          {result.insufficient ? (
            // Insufficient payment - show missing amounts
            <>
              <h2 className="result-title">المبلغ الناقص</h2>
              <div className="result-values">
                <div className="result-item missing-item">
                  <span className="result-label">جديد</span>
                  <span className="result-value num" dir="ltr">
                    {formatArabicNumber(result.missingNewOnly, 0)}
                  </span>
                </div>
                <div className="result-item missing-item">
                  <span className="result-label">قديم</span>
                  <span className="result-value num" dir="ltr">
                    {formatArabicNumber(result.missingOldOnly, 0)}
                  </span>
                </div>
              </div>
            </>
          ) : (
            // Sufficient payment - show change
            <>
              <h2 className="result-title">الباقي</h2>
              <div className="result-values">
                <div className="result-item">
                  <span className="result-label">جديد</span>
                  <span className="result-value num" dir="ltr">
                    {formatArabicNumber(result.changeInNew, 2)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="result-label">قديم</span>
                  <span className="result-value num" dir="ltr">
                    {formatArabicNumber(result.changeInOld, 0)}
                  </span>
                </div>
              </div>

              {/* Breakdown */}
              {result.breakdown && result.breakdown.length > 0 && (
                <div className="breakdown-section">
                  <h3 className="breakdown-title">التفصيل المقترح</h3>
                  <ul className="breakdown-list">
                    {result.breakdown.map((item, index) => (
                      <li key={index}>
                        {item.isRemainder ? (
                          <>
                            والباقي <span className="num" dir="ltr">{formatArabicNumber(item.denomination)}</span> {item.currency}
                          </>
                        ) : (
                          <>
                            <span className="num" dir="ltr">{formatArabicNumber(item.count)}</span>
                            ×
                            <span className="num" dir="ltr">{formatArabicNumber(item.denomination)}</span>
                            {' '}{item.currency}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CurrencyCalculator;
